export const CLIENT_TICK_MS = 17;

export const SEND_POSITION_INTERVAL_MS = 200;

export const PING_INTERVAL_MS = 5000;

export const RECONNECT_DELAY_MS = 3000;

/** Floor for the restart reload delay, and the delay used outright when no end-game result is on screen to wait out. */
export const RESTART_RELOAD_MIN_DELAY_MS = 1500;

export const SERVER_RESTART_NOTICE_TEXT = 'Server restarting for an update.';

export const FIRE_INTERVAL_MS = 100;

export const LERP = {
  playerPosition: 1 / 25,
  playerAngle: 1 / 26,
  droneAngle: 1 / 21,
  soccerBallPosition: 1 / 25,
  cameraZoom: 1 / 5,
  spectatorFollow: 1 / 10,
  gamepadAim: 1 / 5,

  /** Faster than the entity alphas because the feed behind it is: `TeamPositions` lands five times a second, so smoothing at 1/25 would settle a full step behind the ship it marks. */
  teamMarker: 1 / 8,
} as const;

/** Past this gap the smoothing is not catching up to movement, it is animating a teleport — a respawn, a rubber-band, a re-entry into view. Snap instead. Well above any distance a ship covers between updates. */
export const MAX_LERP_DISTANCE = 400;

export const UNSMOOTHED_ENTITY_TYPES = ['Scrap', 'Turret', 'ControlPoint'] as const;

export const DEFAULT_VIEW_DIST = 1800;

export const VIEW_DIST_PER_SHIP = 1800;

export const SPECTATOR_VIEW_DIST = 2800;

export const DEAD_VIEW_DIST_BONUS = 500;

export const SPECTATOR_MOVE_SPEED = 900;

export const SPECTATOR_MIN_VIEW_DIST = 900;

/** Enough margin past the map's limiting axis that the boundary and both bases sit inside the frame rather than on it. */
export const SPECTATOR_FIT_MARGIN = 1.06;

export const SPECTATOR_ZOOM_STEP = 1.18;

export const SPECTATOR_EDGE_PAN_CSS_PX = 26;

export const SPECTATOR_PICK_DEVICE_PX = 70;
export const SPECTATOR_PICK_MIN_WORLD = 90;

/** `lineWidth` is in device pixels and divided by the world scale at draw time, so the ring stays one hairline at every zoom. */
export const FOLLOW_RING = {
  radius: 85,
  lineWidth: 2,
  alpha: 0.5,
  color: '#77EEFF',
} as const;

export function uiScaleFor(worldScale: number): number {
  return (worldScale + 1) / 2;
}

export const MOUSE_DEAD_ZONE_PX = 80;

export const GAMEPAD_DEAD_ZONE = 0.15;
export const GAMEPAD_MOVE_THRESHOLD = 0.9;

/** No server counterpart — the client picks the target and the server only range-checks it, so this is gameplay-authoritative on the client. */
export const TARGET_CONE_HALF_ANGLE = 0.35;
export const SOCCER_BALL_CONE_HALF_ANGLE = 0.7;

export const BASE_TARGET_CONE_HALF_ANGLE = TARGET_CONE_HALF_ANGLE * 3;
export const BASE_TARGET_DISTANCE_BIAS = 1000;

/** Human post-shot slowdown is client-only and unenforced by the server; the bot equivalent (different duration) lives in the simulation. */
export const POST_SHOT_SLOWDOWN_MS = 350;
export const POST_SHOT_SPEED_DIVISOR = 4;

export const SNIPE_LIFETIME_MS = 120;

export const BACKGROUND_PARALLAX = 1 / 8;

export const TRAIL_FADE_ALPHA = 0.97;

export const DEATH_TIMER_MS = 8000;

export const SPRITE_SIZE = {
  playerBase: 100,
  scrap: 40,
  turret: 100,
  drone: 45,
  base: 800,
  soccerBall: 500,
  controlPoint: 500,
} as const;

/** Sprite art points up (-Y), not right (+X); every draw must add this or the whole game renders 90 degrees off its heading. */
export const SPRITE_FACING_OFFSET = Math.PI / 2;

export const HP_BAR_OFFSET = 70;
export const NAME_OFFSET = 74;

export const TAG_SIZE = 30;
export const TAG_GAP = 20;
export const TAG_RISE = { other: 10, self: 6 } as const;

export const SELF_TAG_INSET = 70;

export const MAGNET_RING = { alpha: 0.2, lineWidth: 2, color: '#77EEFF' } as const;

export function hudScaleFor(viewportScaleValue: number): number {
  return (viewportScaleValue + 1) / 2;
}

export function healthBarWidth(hudScale: number): number {
  return 100 * hudScale;
}

export const TEAM_COLORS = ['#57E', '#A22'] as const;

export const GAME_FONT_FAMILY = 'xirod';

export const FONT_FRACTION = { large: 5 / 100, medium: 3 / 100, small: 2 / 100 } as const;
